import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  downloadAccountTemplate,
  getAllMappingsByObjectType,
} from '../../../services/accountService.js';
import { useUser } from '../../../contexts/userContext.js';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../../System/messageManager/messageManager.js';
import { useNameConventions } from '../../../contexts/nameConventionsContext.js';
import {
  extractData,
  MULTI_DATATYPE_UPLOAD_LAST_STEP,
  SINGLE_DATATYPE_UPLOAD_LAST_STEP,
} from './budgetUploadHelpers.js';

const budgetUploadInitialState = {
  activeStep: 0,
  uploadedFiles: [],
  setUploadedFiles: () => {},
};

const BudgetUploadContext = createContext(budgetUploadInitialState);

const BudgetUploadContextProvider = ({ children }) => {
  const { costModel } = useUser();
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [newUpload, setNewUpload] = useState(false);
  const { nameConventions } = useNameConventions();
  const [activeStep, setActiveStep] = useState(0);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [excelColumns, setExcelColumns] = useState({});
  const [parsedData, setParsedData] = useState({});
  const [dataTypes, setDataTypes] = useState([]);
  const [originalData, setOriginalData] = useState({});
  const [personnelMappedCols, setPersonnelMappedCols] = useState(new Map());
  const [nonPersonnelMappedCols, setNonPersonnelMappedCols] = useState(
    new Map(),
  );
  const [savedMappings, setSavedMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [unmappedCols, setUnmappedCols] = useState([]);
  const [showUnmappedPopup, setShowUnmappedPopup] = useState(false);
  const [budgetNameAndYear, setBudgetNameAndYear] = useState('');
  const {user} = useUser();
  const [disablePrimaryButton, setDisablePrimaryButton] = useState(false);
  const [templates, setTemplates] = useState({
    personnel: [],
    nonPersonnel: [],
  });

  useEffect(() => {
    if (!costModel && !templates) {
      ToastComponent.present({
        message:
          messageManager(nameConventions).BUDGET_UPLOAD.GENERAL
            .NO_ORG_HIERARCHY,
      });
      setUploadedFiles([]);
      setUploadMode(false);
      return;
    }
  }, [user.activeBudget]);

  const handleUploadFiles = async (files) => {
    setUploadedFiles(files);
    await prepare();
  };

  const handleParse = async (uploadedFiles) => {
    const parsingResult = await extractData(
      uploadedFiles,
      setParsedData,
      setExcelColumns,
      nameConventions,
      setOriginalData,
      setDataTypes,
      setUploadedFiles
    );
    
    return parsingResult;
  };

  const prepare = async () => {
    try {
      const [templateRes, mappingsRes] = await Promise.all([
        downloadAccountTemplate(),
        getAllMappingsByObjectType(),
      ]);

      setTemplates({
        personnel: templateRes.data.personnel[0],
        nonPersonnel: templateRes.data.nonPersonnel[0],
      });
      if (user.activeBudget) {
        const formattedMappings = (mappingsRes.data || []).map((item) => ({
          ...item,
          mappedValues: new Map(Object.entries(item.mappedValues || {})),
        }));

        setSavedMappings(formattedMappings);
      }
    } catch (error) {
      ToastComponent.present({
        message:
          messageManager(nameConventions).BUDGET_UPLOAD.MAPPING.FETCH_ERROR,
      });
    }
  };

  const resetContext = () => {
    setActiveStep(0);
    setUploadMode(false);
    setUploadedFiles([]);
    setExcelColumns({});
    setParsedData({});
    setDataTypes([]);
    setOriginalData({});
    setPersonnelMappedCols(new Map());
    setNonPersonnelMappedCols(new Map());
    setSelectedMapping(null);
    setUnmappedCols([]);
    setShowUnmappedPopup(false);
    setBudgetNameAndYear('');
    setDisablePrimaryButton(false);
  };

  const cancelChanges = () => {
    setActiveStep(0);
    setPersonnelMappedCols(new Map());
    setNonPersonnelMappedCols(new Map());
    setSelectedMapping(null);
    setUnmappedCols([]);
    setShowUnmappedPopup(false);
    setBudgetNameAndYear('');
    setDisablePrimaryButton(false);
  };

  const determineLastStepAndValidations = (gridRef) => {
    let errors = [];
    gridRef.current.api.forEachNode(node=> {
      if (node.data.Error) {
        errors.push(node.data);
      }
    });

    const isLastStep =
      dataTypes.length === 1 ?
        activeStep === SINGLE_DATATYPE_UPLOAD_LAST_STEP
        : activeStep === MULTI_DATATYPE_UPLOAD_LAST_STEP;
    return isLastStep && !errors.length;
  };

  return (
    <BudgetUploadContext.Provider
      value={{
        activeStep,
        setActiveStep,
        templates,
        uploadMode,
        setUploadMode,
        uploadedFiles,
        setUploadedFiles,
        handleParse,
        parsedData,
        setParsedData,
        excelColumns,
        savedMappings,
        setSavedMappings,
        personnelMappedCols,
        setPersonnelMappedCols,
        nonPersonnelMappedCols,
        setNonPersonnelMappedCols,
        originalData,
        selectedMapping,
        setSelectedMapping,
        dataTypes,
        unmappedCols,
        setUnmappedCols,
        showUnmappedPopup,
        setShowUnmappedPopup,
        resetContext,
        cancelChanges,
        determineLastStepAndValidations,
        budgetNameAndYear, 
        setBudgetNameAndYear,
        disablePrimaryButton, 
        setDisablePrimaryButton,
        handleUploadFiles,
        openUploadDialog,
        setOpenUploadDialog,
        newUpload,
        setNewUpload
      }}
    >
      {children}
    </BudgetUploadContext.Provider>
  );
};

BudgetUploadContextProvider.propTypes = {
  children: PropTypes.node,
};

export default BudgetUploadContextProvider;
export { BudgetUploadContext };
